import { useEffect, useRef, useCallback } from 'react';

import { announceToScreenReader } from '../utils/screenReaderAnnounce';

export const DEFAULT_SEARCH_ANNOUNCEMENT_DELAY_MS = 300;
export const DEFAULT_SEARCH_ANNOUNCEMENT_MESSAGE =
  'Gradebook has been updated.';

/**
 * Announces when an instructor search has finished updating the gradebook.
 * Uses imperative role="alert" injection for reliable Chrome + VoiceOver support.
 */
export function useGradebookSearchAnnouncement({
  searchPhrase,
  searchType,
  gradebookGridData,
  isGradebookFetching,
  isGradebookReFetching,
  isLoading,
  isReady,
  message = DEFAULT_SEARCH_ANNOUNCEMENT_MESSAGE,
  delayMs = DEFAULT_SEARCH_ANNOUNCEMENT_DELAY_MS,
}) {
  const pendingSearchRef = useRef(null);
  const prevSearchRef = useRef({ phrase: searchPhrase, type: searchType });
  const announcementTimerRef = useRef(null);

  const announceSearchComplete = useCallback(() => {
    if (announcementTimerRef.current) {
      clearTimeout(announcementTimerRef.current);
    }

    announcementTimerRef.current = setTimeout(() => {
      announceToScreenReader(message);
      announcementTimerRef.current = null;
    }, delayMs);
  }, [message, delayMs]);

  useEffect(() => {
    return () => {
      if (announcementTimerRef.current) {
        clearTimeout(announcementTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const searchChanged =
      prevSearchRef.current.phrase !== searchPhrase ||
      prevSearchRef.current.type !== searchType;

    if (searchChanged) {
      const hadSearch = Boolean(prevSearchRef.current.phrase);
      const hasSearch = Boolean(searchPhrase);
      if (hasSearch || hadSearch) {
        pendingSearchRef.current = {
          phrase: searchPhrase,
          type: searchType,
          gridAtStart: gradebookGridData,
          hasSeenFetch: false,
        };
      }
      prevSearchRef.current = { phrase: searchPhrase, type: searchType };
    }
  }, [searchPhrase, searchType, gradebookGridData]);

  useEffect(() => {
    const pendingSearch = pendingSearchRef.current;
    if (
      pendingSearch &&
      pendingSearch.phrase === searchPhrase &&
      pendingSearch.type === searchType &&
      (isGradebookFetching || isGradebookReFetching)
    ) {
      pendingSearch.hasSeenFetch = true;
    }
  }, [isGradebookFetching, isGradebookReFetching, searchPhrase, searchType]);

  useEffect(() => {
    const pendingSearch = pendingSearchRef.current;
    const isPendingSearch =
      pendingSearch &&
      pendingSearch.phrase === searchPhrase &&
      pendingSearch.type === searchType;

    if (
      !isPendingSearch ||
      isGradebookFetching ||
      isGradebookReFetching ||
      isLoading ||
      !isReady
    ) {
      return;
    }

    const gridChanged = gradebookGridData !== pendingSearch.gridAtStart;
    const fetchCompleted = pendingSearch.hasSeenFetch;

    if (!gridChanged && !fetchCompleted) {
      return;
    }

    pendingSearchRef.current = null;
    announceSearchComplete();
  }, [
    isGradebookFetching,
    isGradebookReFetching,
    isLoading,
    isReady,
    gradebookGridData,
    searchPhrase,
    searchType,
    announceSearchComplete,
  ]);
}
